import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Modal, Input, List, Avatar, Flex, Typography, Button } from 'antd'
import {
  SearchOutlined,
  CloseOutlined,
  RightOutlined,
  LoadingOutlined,
  FileSearchOutlined,
  InboxOutlined,
  AccountBookOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { useSelector } from 'react-redux'
import {
  darkModeSelector,
  transTypeConfigSelector,
  mobileViewSelector,
  translationMenuSelector,
} from 'app-redux/selectors'
import { useIntl } from 'react-intl'
import { isEmpty, size } from 'lodash-es'
import { Link } from 'react-router-dom'
import { useInfiniteGlobalSearch } from '@/utils/queries'
import useDebounceValue from '@/utils/hooks/useDebounceValue'
import colors from '../FinanceAccounts/Charts/colors'
import { generateCardName } from '@/utils/helper'
import { getPermissions } from '@/redux/user/selectors'
import { getTransTypeUri } from '@/utils/transTypeUri'
import { GLOBAL_SEARCH_TYPE, transType as transTypeStatic } from '@/utils/static'

import styles from './style.module.css'

const WITHHOLDING_TRANS_TYPES = [
  transTypeStatic.INVOICE_WITHOLDING,
  transTypeStatic.PURCHASE_INVOICE_WITHOLDING,
  transTypeStatic.SPEND_WITHOLDING,
  transTypeStatic.INVOICE_PAYMENT_WITHOLDING,
  transTypeStatic.PURCHASE_INVOICE_PAYMENT_WITHOLDING,
  transTypeStatic.PROFORMA_ORDER_PAYMENT_WITHOLDING,
  transTypeStatic.PROFORMA_PAYMENT_WITHOLDING,
  transTypeStatic.EXPENSE_WITHOLDING,
  transTypeStatic.EXPENSE_PAYMENT_WITHOLDING,
  transTypeStatic.JOIN_INVOICE_PAYMENT_WITHOLDING,
  transTypeStatic.PURCHASE_JOIN_INVOICE_PAYMENT_WITHOLDING,
]

const GLOBAL_SEARCH_AVATAR_ICON = {
  [transTypeStatic.GLOBAL_SEARCH_ACCOUNT]: AccountBookOutlined,
  [transTypeStatic.GLOBAL_SEARCH_PRODUCT]: ShoppingOutlined,
  [transTypeStatic.GLOBAL_SEARCH_CONTACT]: TeamOutlined,
}

const { Text } = Typography

const getTranslationResource = (menu, resource) => {
  if (!isEmpty(menu)) {
    return menu[resource]
  }
  return ''
}

const GlobalSearch = ({ open, onCancel }) => {
  const { formatMessage } = useIntl()
  const darkMode = useSelector(darkModeSelector)
  const transTypes = useSelector(transTypeConfigSelector)
  const permissions = useSelector(getPermissions)
  const isMobileView = useSelector(mobileViewSelector)
  const translationMenu = useSelector(translationMenuSelector)

  // const { onNavigate } = useNavigation()

  const [search, setSearch] = useState('')
  const inputRef = useRef(null)
  const debouncedSearch = useDebounceValue(search, 400)
  const normalizedTransTypes = useMemo(
    () => (Array.isArray(transTypes) ? transTypes : []),
    [transTypes],
  )

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteGlobalSearch(
      { search: debouncedSearch },
      { enabled: size(debouncedSearch) >= 3 && open },
    )

  const _data = useMemo(() => {
    if (!data) {
      return []
    }
    const pages = data.pages || []
    const __data = pages.flatMap((page) => {
      if (Array.isArray(page?.data)) {
        return page.data.map((item) => {
          let transTypeName = ''
          if (GLOBAL_SEARCH_TYPE[item.transTypeId]) {
            transTypeName = getTranslationResource(
              translationMenu,
              GLOBAL_SEARCH_TYPE[item.transTypeId],
            )
          } else {
            const transType = normalizedTransTypes.find((row) => row.id === item.transTypeId)
            transTypeName = transType?.name || ''
          }

          return {
            ...item,
            transTypeName,
          }
        })
      }
      return []
    })
    return __data
  }, [data, normalizedTransTypes, translationMenu])

  const handleSearchChange = useCallback((e) => {
    const { value } = e.target
    setSearch(value)
  }, [])

  const handleScroll = useCallback(
    (e) => {
      const { target } = e
      const { scrollTop, scrollHeight, clientHeight } = target

      // Load more ketika user scroll mendekati bottom (200px dari bottom)
      if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  const handleReset = useCallback(() => {
    setSearch('')
    inputRef.current?.focus()
  }, [])

  const renderItem = useCallback(
    (item) => {
      let warehouseId = item.tran?.warehouse_id
      if (item.transTypeId === transTypeStatic.WAREHOUSE_TRANSFER) {
        warehouseId = item.tran?.from_warehouse_id
      }
      let _id = item.id
      if (item.transTypeId === transTypeStatic.BANK_MANUAL_JOURNAL) {
        _id = item.tran?.business_tran_id
      }
      let url = getTransTypeUri(
        item.transTypeId,
        {
          id: _id,
          accountId: item.tran?.bank_account_id,
          // cabang SEND_TRANSFER di getTransTypeUri membaca `bank_account_id`, bukan `accountId`
          bank_account_id: item.tran?.bank_account_id,
          warehouseId,
        },
        permissions,
      )
      // pemotongan tidak punya URL di getTransTypeUri; samakan dengan list mutasi bank
      // (DetailFinanceBank) yang membukanya ke detail kas bank
      if (
        WITHHOLDING_TRANS_TYPES.includes(item.transTypeId) &&
        item.tran?.bank_account_id &&
        permissions.includes('cash_bank')
      ) {
        url = `/banks/${item.tran.bank_account_id}/transaction/detail/${_id}`
      }
      const GlobalSearchAvatarIcon = GLOBAL_SEARCH_AVATAR_ICON[item.transTypeId]
      return (
        <Link to={url} onClick={onCancel}>
          <List.Item
            className={styles.listItem}
            actions={[
              <RightOutlined
                style={{
                  color: darkMode ? '#B8BECA' : 'rgba(0,0,0,.25)',
                  fontSize: 16,
                  marginRight: 16,
                }}
                key="arrowRight"
              />,
            ]}
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  style={{ background: colors[(item.transTypeId || 0) % colors.length] }}
                  icon={GlobalSearchAvatarIcon ? <GlobalSearchAvatarIcon /> : undefined}
                >
                  {!GlobalSearchAvatarIcon && generateCardName(item.transTypeName)}
                </Avatar>
              }
              title={item.transTypeName}
              description={
                <Highlighter
                  highlightClassName="highlight"
                  searchWords={[search]}
                  autoEscape
                  textToHighlight={item.words}
                />
              }
              style={{ paddingLeft: 16, paddingRight: 16 }}
            />
          </List.Item>
        </Link>
      )
    },
    [permissions, darkMode, search, onCancel],
  )

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={
        <Flex>
          <Text>{formatMessage({ id: 'total_transactions_found' }, { count: _data.length })}</Text>
        </Flex>
      }
      width={isMobileView ? '100%' : 900}
      closable={false}
      styles={{
        content: { padding: 0, overflow: 'hidden' },
        body: { padding: 0, height: 600 },
        wrapper: isMobileView ? { maxWidth: '100%' } : undefined,
        footer: {
          background: darkMode ? '#141414' : '#fafafa',
          paddingLeft: 24,
          marginTop: 0,
          paddingRight: 24,
          paddingTop: 16,
          paddingBottom: 16,
        },
      }}
    >
      <Flex style={{ height: 600 }}>
        {/* Panel Kiri - hanya di desktop */}
        {!isMobileView && (
          <div
            className={styles.leftPanel}
            style={{
              background: darkMode ? '#2c4eb3' : '#4677f3',
              width: 300,
              padding: 40,
              flexShrink: 0,
            }}
          >
            <div className={styles.accentBottomRight} />
            <div className={styles.iconArea}>
              <div className={styles.accentTopLeft} />
              <div className={styles.iconAccentTransparent} />
              <div className={styles.illustrationCircle}>
                <img
                  src="/resources/images/icons/search.svg"
                  alt="search"
                  className={styles.iconImage}
                />
              </div>
            </div>
            <div className={styles.textBlock}>
              <Typography.Title level={3} className={styles.panelTitle}>
                {formatMessage({ id: 'globalSearch_find_everything' })}
              </Typography.Title>
              <Text className={styles.panelDesc}>
                {formatMessage({ id: 'globalSearch_find_everything_desc' })}
              </Text>
            </div>
          </div>
        )}

        {/* Panel Kanan */}
        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              padding: 24,
              paddingBottom: 16,
              background: darkMode ? '#1f1f1f' : '#fff',
              borderBottom: `1px solid ${darkMode ? '#303030' : '#f0f0f0'}`,
            }}
          >
            <Flex justify="flex-end" style={{ marginBottom: 16 }}>
              <CloseOutlined className={styles.closeModalIcon} onClick={onCancel} />
            </Flex>
            <span
              style={{
                display: 'inline-block',
                background: '#fef5d0',
                color: darkMode ? '#1f1f1f' : '#b88842',
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 12px',
                borderRadius: 6,
                marginBottom: 16,
                letterSpacing: '0.5px',
              }}
            >
              {formatMessage({ id: 'globalSearch_data_search' })}
            </span>
            <Typography.Title level={4} style={{ margin: '0 0 20px 0', fontWeight: 600 }}>
              {formatMessage({ id: 'globalSearch_what_are_you_looking_for' })}
            </Typography.Title>
            <Input
              value={search}
              onChange={handleSearchChange}
              autoFocus
              size="large"
              placeholder={formatMessage({ id: 'globalSearch_search_placeholder' })}
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: 8 }} />}
              style={{
                borderRadius: 8,
                border: `1px solid ${darkMode ? '#434343' : '#91caff'}`,
                padding: '10px 16px',
              }}
              allowClear
              ref={inputRef}
            />
          </div>

          {!search || size(search) < 3 ? (
            /* Empty state - persis seperti gambar 4 */
            <div
              className={styles.emptyStateContent}
              style={{
                background: darkMode ? '#141414' : '#fff',
              }}
            >
              <Avatar
                size={72}
                icon={
                  <FileSearchOutlined
                    style={{
                      fontSize: 36,
                      color: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                    }}
                  />
                }
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.08)' : '#f9fbfd',
                  marginBottom: 24,
                }}
              />
              <Text
                type="secondary"
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                {formatMessage({ id: 'globalSearch_search_hint' })}
              </Text>
              <div className={styles.escPill}>
                <span className={styles.escPillKey}>ESC</span>
                <span className={styles.escPillText}>
                  {formatMessage({ id: 'globalSearch_esc_to_close' }).replace('ESC ', '')}
                </span>
              </div>
            </div>
          ) : !isLoading && _data.length === 0 ? (
            /* Hasil pencarian kosong - pesan deskriptif + tombol reset */
            <div
              className={styles.emptyStateContent}
              style={{
                background: darkMode ? '#141414' : '#fff',
              }}
            >
              <Avatar
                size={72}
                icon={
                  <InboxOutlined
                    style={{
                      fontSize: 36,
                      color: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                    }}
                  />
                }
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.08)' : '#f9fbfd',
                  marginBottom: 24,
                }}
              />
              <Text
                type="secondary"
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  textAlign: 'center',
                  maxWidth: 320,
                  marginBottom: 20,
                }}
              >
                {formatMessage({ id: 'globalSearch_no_data_with_keyword' }, { keyword: search })}
              </Text>
              <Button type="primary" ghost onClick={handleReset}>
                {formatMessage({ id: 'globalSearch_reset_search' })}
              </Button>
            </div>
          ) : (
            <div
              onScroll={handleScroll}
              style={{
                flex: 1,
                minHeight: 300,
                maxHeight: 600,
                overflowY: 'auto',
              }}
            >
              <List
                loading={isLoading}
                itemLayout="horizontal"
                dataSource={_data}
                renderItem={renderItem}
                rowKey="id"
              />
              {isFetchingNextPage && (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <LoadingOutlined spin />
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {formatMessage({ id: 'loading_more' })}
                  </Text>
                </div>
              )}
            </div>
          )}
        </Flex>
      </Flex>
    </Modal>
  )
}

export default GlobalSearch
